import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { LoginRequest, LoginResponse, TenantResponse, TenantRole, TenantsAvailable, UserInfo } from '@models'
import { environment } from '@env'
import { TenantService } from '@services'

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private tenantService: TenantService,
    private http: HttpClient
  ) {}

  async login(data: LoginRequest): Promise<boolean> {
    const url = `${environment.cms_latam_api.baseUrl}/auth/auth/v2/login`
    const resp = await firstValueFrom(this.http.post<LoginResponse>(url, data))
    const token = resp?.token?.access_token ?? null
    if (token) {
      localStorage.setItem('access_token', token)
      await this.setTenantsAvaibles()
    }
    return this.isLoggedIn()
  }

  getTenantsAvaibles(): TenantsAvailable[] {
    const tenantsAvailableData = localStorage.getItem('tenants_available')
    if (tenantsAvailableData == null) return []

    const tenantsAvailable: TenantsAvailable[] = JSON.parse(tenantsAvailableData)
    return tenantsAvailable
  }

  async setTenantsAvaibles(): Promise<void> {
    const tenantsAvailable = []

    const userInfo = await this.getUserInfo()
    if (userInfo == null || userInfo.Tenants == null) return

    const allTenants = await this.tenantService.getAll()

    for (let i = 0; i < allTenants.length; i++) {
      const tenant: TenantResponse = allTenants[i]

      if (userInfo?.Tenants.filter((x) => x.Id == tenant.id).length) {
        const tenantDropdown: TenantsAvailable = {
          id: tenant.id,
          name: tenant.brand.name + ' ' + tenant.country.name,
          flag: tenant.country.name.toLowerCase() + '-flag.png',
        }

        tenantsAvailable.push(tenantDropdown)
      }
    }

    tenantsAvailable.sort((a, b) => {
      const countryA = a.name.split(' ').pop() ?? ''
      const countryB = b.name.split(' ').pop() ?? ''

      const isBrasilA = countryA === 'Brasil'
      const isBrasilB = countryB === 'Brasil'

      if (isBrasilA !== isBrasilB) return isBrasilA ? -1 : 1

      if (countryA !== countryB) return countryA.localeCompare(countryB)

      const isEluxA = a.name.startsWith('Electrolux')
      const isEluxB = b.name.startsWith('Electrolux')

      if (isEluxA !== isEluxB) return isEluxA ? -1 : 1

      return a.name.localeCompare(b.name)
    })

    if (localStorage.getItem('tenant_active') === null)
      localStorage.setItem('tenant_active', tenantsAvailable[0].id.toString())

    localStorage.setItem('tenants_available', JSON.stringify(tenantsAvailable))
  }

  async getTenantActive() {
    var tenants = await this.getTenantsAvaibles()
    return tenants?.find((t) => t.id.toString() == localStorage.getItem('tenant_active')) || null
  }

  getToken(): string | null {
    return localStorage.getItem('access_token')
  }

  getUserInfo(): UserInfo | null {
    let userInfoData = localStorage.getItem('user_info')
    if (userInfoData == null) this.setUserInfoFromToken()

    userInfoData = localStorage.getItem('user_info')
    if (userInfoData == null) return null

    const userInfo = JSON.parse(userInfoData)
    return userInfo
  }

  setUserInfoFromToken(): void {
    const token = this.getToken()
    if (!token) return

    const clean = token.startsWith('Bearer ') ? token.slice(7) : token

    const base64Url = clean.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    const parsed = JSON.parse(jsonPayload)

    let tenants: TenantRole[] = []
    try {
      if (typeof parsed.Tenants === 'string') {
        tenants = JSON.parse(parsed.Tenants) as TenantRole[]
      }
    } catch {
      tenants = []
    }

    const claims: UserInfo = {
      Id: parsed.Id,
      Name: parsed.Name,
      Email: parsed.Email,
      Tenants: tenants,
    }

    const userInfo = JSON.stringify(claims)
    localStorage.setItem('user_info', userInfo)
  }

  setUserInfo(userInfo: UserInfo): void {
    const userInfoString = JSON.stringify(userInfo)
    localStorage.setItem('user_info', userInfoString)
  }

  setTenantActive(tenant: TenantsAvailable) {
    localStorage.setItem('tenant_active', tenant.id.toString())
    window.location.reload()
  }

  private clearAuthData(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('tenant_active')
    localStorage.removeItem('user_info')
    localStorage.removeItem('tenants_available')
  }

  isLoggedIn() {
    const token = this.getToken()
    if (!token) {
      this.clearAuthData()
      return false
    }

    try {
      const clean = token.startsWith('Bearer ') ? token.slice(7) : token
      const tokenParts = clean.split('.')
      if (tokenParts.length < 2) {
        this.clearAuthData()
        return false
      }

      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      const payload = JSON.parse(jsonPayload)
      const exp = Number(payload?.exp)

      if (!Number.isFinite(exp)) return true

      const isValid = exp * 1000 > Date.now()
      if (!isValid) this.clearAuthData()
      return isValid
    } catch {
      this.clearAuthData()
      return false
    }
  }

  logout() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('tenant_active')
    localStorage.removeItem('user_info')
    localStorage.removeItem('tenants_available')
    localStorage.clear()
    window.location.reload()
  }
}
